/**
 * SafetySignoffNotice.tsx — tells STAFF when the safety wording behind a
 * medication list has not yet been ratified by the clinical lead.
 *
 * Owner repo: @thegpservice/gpsclinics-portal.
 *
 * WHY THIS EXISTS AS A COMPONENT RATHER THAN A LINE OF JSX. The pinned contract
 * is explicit about it — `medicationSafety.clinicallyApproved` carries
 * "consumers must surface the un-ratified state to staff; it is never hidden".
 * That is a contract obligation on EVERY staff surface that reads plan options,
 * and there is more than one of them. A line of JSX copied into each is a line
 * that gets copied into two of the three next time somebody adds a surface; a
 * component that takes the options and decides for itself cannot be
 * half-applied.
 *
 * WHAT IT IS NOT. It is not a warning about the MEDICINE — the drug is licensed
 * and the prescribing decision is unaffected. It is a note about OUR COPY: the
 * risk wording a patient was shown before they paid has not yet been signed off
 * by the clinical lead. A prescriber reading it should cover the risks in their
 * own words rather than assume the patient has already had the final version.
 *
 * Rendered as `role="status"`, never `role="alert"`. On the clinician surfaces
 * the R31 CrisisBanner is the ONE assertive region — a second one competing with
 * a safeguarding alert for the same announcement queue is exactly how the
 * important one gets missed. Same rule KeyAlerts.tsx already follows.
 *
 * Renders NOTHING when every option is ratified, and nothing when there are no
 * options at all: a permanent banner is furniture, and furniture is not read.
 */
import type { PlanOption } from '../api/types';

export function SafetySignoffNotice({ options }: { options: readonly PlanOption[] }) {
  // Only an EXPLICIT `false` counts. An option whose safety block is absent is a
  // different problem (the patient-facing gate refuses the sale outright), and
  // reporting it here as "awaiting sign-off" would tell a clinician the wording
  // exists but is unratified when in fact there is none.
  const unratified = options.filter((option) => option.safety?.clinicallyApproved === false);
  if (unratified.length === 0) return null;

  return (
    <div
      role="status"
      style={{
        background: 'var(--gps-warn-bg)',
        color: 'var(--gps-warn-text)',
        border: '1px solid var(--gps-border)',
        borderLeft: '4px solid var(--gps-warn-text)',
        borderRadius: 'var(--gps-r-md)',
        padding: '10px 14px',
        margin: '0 0 var(--gps-s-3)',
        fontSize: '13.5px',
        lineHeight: 1.5,
      }}
    >
      <strong>Patient safety wording is awaiting clinical sign-off</strong> for{' '}
      {unratified.length === options.length
        ? 'these treatments'
        : `${unratified.length} of these treatments`}
      . The medicines themselves are unaffected — this is about the risk information the patient was
      shown before paying. Cover the risks in your own words rather than assuming they have seen the
      final version.
    </div>
  );
}
